import { getBusinessCategory } from './businessCategories';

const DAY_KEYS = {
  MONDAY: 'monday',
  TUESDAY: 'tuesday',
  WEDNESDAY: 'wednesday',
  THURSDAY: 'thursday',
  FRIDAY: 'friday',
  SATURDAY: 'saturday',
  SUNDAY: 'sunday'
};

const formatGoogleTime = (time) => {
  const hour = Number(time?.hours);
  const minute = Number(time?.minutes || 0);
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return null;

  return `${String(hour % 24).padStart(2, '0')}:${String(minute).padStart(
    2,
    '0'
  )}`;
};

const timeToMinutes = (time) => {
  const [hour, minute] = time.split(':').map(Number);
  return hour * 60 + minute;
};

const toBusinessHours = (periods = []) => {
  const days = Object.values(DAY_KEYS).reduce((result, day) => {
    result[day] = {
      isOpen: false,
      open24: false,
      openTime: '09:00',
      closeTime: '17:00'
    };
    return result;
  }, {});

  periods.forEach((period) => {
    const day = DAY_KEYS[period.openDay];
    const openTime = formatGoogleTime(period.openTime);
    const closeTime = formatGoogleTime(period.closeTime);
    if (!day || !openTime || !closeTime) return;

    const current = days[day];
    const is24Hours =
      openTime === '00:00' &&
      closeTime === '00:00' &&
      period.closeDay === period.openDay;
    days[day] = {
      isOpen: true,
      open24: current.isOpen ? current.open24 || is24Hours : is24Hours,
      openTime:
        current.isOpen &&
        !is24Hours &&
        timeToMinutes(current.openTime) < timeToMinutes(openTime)
          ? current.openTime
          : openTime,
      closeTime:
        current.isOpen &&
        !is24Hours &&
        timeToMinutes(current.closeTime) > timeToMinutes(closeTime)
          ? current.closeTime
          : closeTime
    };
  });

  return { days };
};

const formatAddress = (address) => {
  if (typeof address === 'string') return address;
  if (!address) return '';

  return [
    ...(address.addressLines || []),
    address.locality,
    address.administrativeArea,
    address.postalCode
  ]
    .filter(Boolean)
    .join(', ');
};

const getCategoryName = (location) =>
  location.categories?.primaryCategory?.displayName ||
  location.primaryCategory?.displayName ||
  location.category?.displayName ||
  location.category ||
  '';

const getStructuredServiceName = (serviceTypeId) =>
  serviceTypeId
    .replace(/^[^:]+:/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());

const getServices = (location) => {
  const serviceItems = location.serviceItems || location.services;
  if (!Array.isArray(serviceItems)) return '';

  return serviceItems
    .map((service) => {
      if (typeof service === 'string') return service;

      const freeFormLabel = service.freeFormServiceItem?.label;
      const structuredService = service.structuredServiceItem;
      const freeFormName =
        typeof freeFormLabel === 'string'
          ? freeFormLabel
          : freeFormLabel?.displayName;
      const structuredName =
        structuredService?.label?.displayName ||
        (structuredService?.serviceTypeId
          ? getStructuredServiceName(structuredService.serviceTypeId)
          : '');

      return (
        freeFormName ||
        structuredName ||
        service.label?.displayName ||
        service.label ||
        service.displayName ||
        service.name ||
        structuredService?.description ||
        service.description
      );
    })
    .filter(Boolean)
    .join('\n');
};

export const mapGoogleLocationToAnswers = (location = {}) => {
  const answers = {};
  const name = location.title || location.locationName;
  const phone =
    location.phoneNumber ||
    location.phoneNumbers?.primaryPhone ||
    location.primaryPhone;
  const secondaryPhone = location.phoneNumbers?.additionalPhones?.[0];
  const website = location.websiteUrl || location.websiteUri;
  const address = formatAddress(
    location.address || location.storefrontAddress || location.formattedAddress
  );
  const serviceArea =
    location.serviceArea?.places?.placeInfos ||
    location.serviceArea?.places ||
    location.serviceAreas;
  const areas = Array.isArray(serviceArea)
    ? serviceArea
        .map((place) =>
          typeof place === 'string'
            ? place
            : place.placeName || place.name || place.locality
        )
        .filter(Boolean)
        .join('\n')
    : '';
  const description =
    location.profile?.description || location.description || '';
  const openingYear = location.openInfo?.openingDate?.year;
  const category = getCategoryName(location);
  const services = getServices(location);

  if (name) answers.name = name;
  if (phone) answers.phone = phone;
  if (secondaryPhone) answers.phone2 = secondaryPhone;
  if (website) answers.website = { url: website, none: false };
  if (address) answers.address = { street: address, suite: '' };
  if (areas) answers.areas = areas;
  if (description) answers.special = description;
  if (Number.isInteger(openingYear) && openingYear > 0) {
    answers.yearStarted = String(openingYear);
  }

  const businessType = getBusinessCategory(category);
  if (businessType) answers.businessType = businessType;
  if (services) answers.services = services;

  if (location.regularHours?.periods) {
    answers.hours = toBusinessHours(location.regularHours.periods);
  }

  return answers;
};

export const canContinueAfterGoogleConnection = (
  isConnected,
  locations,
  selectedLocationKey
) => isConnected && (locations.length === 0 || Boolean(selectedLocationKey));

const isTrue = (value) =>
  value === true || value === 1 || value === '1' || value === 'true';

export const hasGoogleBusinessConnection = (client = {}) => {
  const socialNetworks = client?.socialnetworks || {};

  return (
    isTrue(socialNetworks.googleMyBusiness) ||
    Boolean(
      socialNetworks.googleMyBusinessLocationId ||
        socialNetworks.googleMyBusinessLocationName ||
        socialNetworks.googleLocationId ||
        socialNetworks.googleLocationName
    )
  );
};

export const isGoogleLocationConnectionTimeout = (error) =>
  error?.code === 'ECONNABORTED' ||
  error?.code === 'ETIMEDOUT' ||
  error?.response?.status === 504 ||
  /timed out|timeout/i.test(error?.message || '');
